// Studio 011 (#41, spec 011): the street's ground drawn from the world's cells, never from a painting. Research D
// (studio/research/2026-10-31-art-in-register.md): R4 sub-cell edges from cell data, R5 stochastic tiling and decals, R7
// small seamless AI textures. The painted ground plates drifted off the walkable map wherever the world changed, seamed
// between tiles and invented holes; every pixel here is chosen per cell from the current world, so it cannot drift.
//
// A chunk's ground (32 x 32 cells) is baked ONCE on the GPU into a render texture at GRES texels per logical px:
//   - material: each outdoor cell's class (asphalt < lot < alley < kerb < sidewalk < pavers < grass < dirt). For each
//     priority level a cumulative field (cells at or above it) is blurred (sigma 1.3 cells) on the CPU and sampled
//     bilinearly in the shader; a pixel's class is the highest level whose field passes 0.5. A diagonal street's stepped
//     cells become a straight edge, corners round like a real kerb, and a one-cell strip (a kerb between road and lot)
//     is kept by pinning thin cells (the rule `thin` below). Soft ground (alley, grass, dirt) gets a ragged threshold.
//   - texture: each class samples 4 Seedream variants (tools/art/groundkit.py) by hex tiling (Heitz-Neyret, Mikkelsen's
//     triangle grid): every hex vertex has its own rotation and offset; the variant is a proper colouring of the
//     triangle lattice ((i + 2j) mod 3, and the 4th variant on local hash minima), so no hex shares a variant with a
//     neighbour. A 6 m tone drift (macroTone, as before) on top.
//   - patterns drawn on the world grid: the sidewalk's 1.5 m slabs and joints, Whyte's herringbone pavers (a clay patch
//     per brick), the kerb's lip, its face on the camera side and its shadow in the gutter.
//   - paint: centre and edge lines are polylines fitted to the line_y / line_w cell runs (fitLines, once per map) and
//     drawn from a signed distance texture; crossings are the world's own crossing cells (they are the bars).
//   - decals from data (a 2 m grid, one candidate per decal kind per grid cell, a deterministic hash): cracks, patches,
//     potholes, manholes and oil on asphalt; drains in the gutter, turned along the kerb; gum and litter on sidewalks;
//     weeds in lots; leaves on everything in the fall. A decal draws only on pixels of the class at its centre, so paint
//     never crosses a region boundary.
// Indoor floors keep the CPU painter (iso_render.ts groundRows), composited under the kit.
import { Assets, BufferImageSource, Geometry, GlProgram, Mesh, RenderTexture, Shader, Sprite, Texture, UniformGroup, type Renderer } from 'pixi.js';
import type { IsoMap } from '../../shared/src/iso';
import { CHUNK, B, N, LN, CLASSES, Prep, chunkData, kernel, decalTable } from './groundprep';

const Q = new URLSearchParams(location.search);
/** ground texels per logical px (the plates were 3; a phone at scale 3 draws ~1.5 device px per texel at 2) */
export const GRES = Math.max(1, Math.min(3, Number(Q.get('gres')) || 2));
const DENS: Record<string, number> = { asphalt: 0.6, lot: 0.5, alley: 0.4, curb: 0, sidewalk: 0.3, pavers: 0.32, grass: 0.4, dirt: 0.2, leaves: 0.22, gutter: 0.1 };

// GLSL ES 1.00 in Pixi's dialect (in/out/texture/finalColor; Pixi maps them for WebGL1): the studio's GPU worker and
// some phones have no WebGL2, so no uint, bit ops, texelFetch or const arrays (paintblock.ts keeps a GL1 path too)
const VERT = `in vec2 aPosition;
in vec2 aW;
out vec2 vW;
uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
void main(void) {
  mat3 m = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((m * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
  vW = aW;
}`;

function fragSrc(atl: number[], kitW: number, kitH: number, decW: number, decH: number, dcols: number, dslot: number, dmW: number) {
  const f = (v: number) => v.toFixed(1);
  return `in vec2 vW;
uniform sampler2D uCell;
uniform sampler2D uF1;
uniform sampler2D uF2;
uniform sampler2D uF3;
uniform sampler2D uLine;
uniform sampler2D uKit;
uniform sampler2D uDecal;
uniform sampler2D uDM;
uniform vec4 uOrg;   // window origin (cells) x, y; N; 0
uniform vec4 uOpt;   // leaves on, decals on
const float SLOT = 320.0, GUT = 10.0, SPANC = 5.0, NW = ${f(N)}, DMW = ${f(dmW)};
const vec2 KIT = vec2(${f(kitW)}, ${f(kitH)}), DEC = vec2(${f(decW)}, ${f(decH)});
const float DCOLS = ${f(dcols)}, DSLOT = ${f(dslot)};
const vec3 NIGHT = vec3(15.0, 14.0, 34.0) / 255.0;

float atl(float c) { return c < 0.5 ? ${f(atl[0])} : c < 1.5 ? ${f(atl[1])} : c < 2.5 ? ${f(atl[2])} : c < 3.5 ? ${f(atl[3])} : c < 4.5 ? ${f(atl[4])} : c < 5.5 ? ${f(atl[5])} : c < 6.5 ? ${f(atl[6])} : ${f(atl[7])}; }
// texels-per-cell multiplier: alley gravel and dirt finer (blind A/B, lane5_2: the pebbles read at about twice real size;
// this was a divisor before, so the 1.3 "finer" had made them coarser)
float scl(float c) { return abs(c - 2.0) < 0.5 ? 1.8 : abs(c - 7.0) < 0.5 ? 1.5 : 1.0; }   // (2.2 averaged the stones into a brown smudge on desktop)
// on-screen level per class, against today's plates at the same spots (after-d vs v41/vd, 2026-11-01): the kit's asphalt
// read 52/55/60 where the plates read 63/65/71, its pavers 129/80/71 against 159/100/87 (their joints and bevels darken them)
float gain(float c) { return c < 0.5 ? 1.18 : abs(c - 5.0) < 0.5 ? 1.2 : 1.0; }
// a hash of an integer lattice point (Hoskins' hash12 without sine: stable for coordinates in the thousands)
float h1(vec2 p, float s) {
  vec3 p3 = fract(vec3(p.xyx + s * vec3(17.13, 31.71, 7.97)) * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
vec2 h2(vec2 p, float s) { return vec2(h1(p, s), h1(p, s + 101.0)); }
float vn(vec2 p, float s) {
  vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(h1(i, s), h1(i + vec2(1.0, 0.0), s), f.x), mix(h1(i + vec2(0.0, 1.0), s), h1(i + vec2(1.0, 1.0), s), f.x), f.y);
}
vec3 kit(float cls, float v, vec2 c) {
  float s = atl(cls) * 4.0 + v;
  vec2 l = fract(c / SPANC) * SLOT + GUT;
  vec2 o = vec2(mod(s, 6.0), floor(s / 6.0)) * (SLOT + 2.0 * GUT);
  return texture(uKit, (o + l) / KIT).rgb;
}
float variantOf(vec2 v) {
  float c3 = mod(v.x + 2.0 * v.y, 3.0);
  float h = h1(v, 5.0);
  if (h < 0.35 && h < h1(v + vec2(1.0, 0.0), 5.0) && h < h1(v + vec2(-1.0, 0.0), 5.0) && h < h1(v + vec2(0.0, 1.0), 5.0)
      && h < h1(v + vec2(0.0, -1.0), 5.0) && h < h1(v + vec2(1.0, -1.0), 5.0) && h < h1(v + vec2(-1.0, 1.0), 5.0)) return 3.0;
  return c3;
}
// each hex vertex reads its own patch CENTRED on the vertex: the swatch's wrap seam (where the mip level jumps, a 1 px
// line) then lies 2.5 cells out, where that vertex's weight (w^4 of a 2.6-cell triangle grid) is under 1%
const float HEX = 2.6;
// one bake pixel covers DX x DY in cells (a 2:1 iso pixel at GRES texels a logical px) and ~3 x 6 kit texels: six taps
// spread over the pixel stand in for the mip level the hex seams rule out
const vec2 DX = vec2(1.0, -1.0) / ${f(16 * GRES)}, DY = vec2(1.0, 1.0) / ${f(8 * GRES)};
vec3 kitAA(float cls, float v, vec2 u, mat2 R) {
  vec2 ax = R * DX * 0.25, ay = R * DY * (1.0 / 3.0);
  return (kit(cls, v, u - ax - ay) + kit(cls, v, u + ax - ay) + kit(cls, v, u - ax) + kit(cls, v, u + ax) + kit(cls, v, u - ax + ay) + kit(cls, v, u + ax + ay)) / 6.0;
}
vec3 hexOne(float cls, vec2 v, vec2 c) {
  float a = h1(v, 11.0) * 6.2831853;
  mat2 R = mat2(cos(a), sin(a), -sin(a), cos(a)) * scl(cls);
  vec2 vp = vec2(v.x, (v.y + 0.57735027 * v.x) / 1.15470054) * HEX;
  return kitAA(cls, variantOf(v), R * (c - vp) + SPANC * 0.5 + (h2(v, 13.0) - 0.5) * 0.6, R);
}
// hex tiling on Mikkelsen's skewed triangle grid: three patches a pixel, each with its own rotation and offset
vec3 hexKit(float cls, vec2 c) {
  vec2 st = c / HEX;
  vec2 sk = vec2(st.x, -0.57735027 * st.x + 1.15470054 * st.y);
  vec2 b = floor(sk); vec3 t = vec3(fract(sk), 0.0); t.z = 1.0 - t.x - t.y;
  float s = step(0.0, -t.z), s2 = 2.0 * s - 1.0;
  vec3 w = vec3(-t.z * s2, s - t.y * s2, s - t.x * s2);
  vec3 c1 = hexOne(cls, b + vec2(s, s), c), c2 = hexOne(cls, b + vec2(s, 1.0 - s), c), c3 = hexOne(cls, b + vec2(1.0 - s, s), c);
  w = w * w * w * w; w /= (w.x + w.y + w.z);
  return c1 * w.x + c2 * w.y + c3 * w.z;
}
// Whyte's herringbone, laid at 45 degrees on the world grid: 20 x 10 cm bricks, a clay patch and a tone per brick
vec3 pavers(vec2 c) {
  vec2 q = vec2(c.x + c.y, c.y - c.x) * 0.70710678 / 0.3;   // (half a brick: 0.3 cells; at 0.2 the bond read as noise beside the plates')
  vec2 f = floor(q); float m = mod(f.x - f.y, 4.0);
  vec2 o; float hor;
  if (m < 0.5) { o = f; hor = 1.0; } else if (m < 1.5) { o = f - vec2(1.0, 0.0); hor = 1.0; }
  else if (m > 2.5) { o = f; hor = 0.0; } else { o = f - vec2(0.0, 1.0); hor = 0.0; }
  vec2 l = q - o, sz = hor > 0.5 ? vec2(2.0, 1.0) : vec2(1.0, 2.0);
  float e = min(min(l.x, sz.x - l.x), min(l.y, sz.y - l.y));
  vec2 id = o * 2.0 + vec2(hor, 0.0);
  float hb = h1(id, 71.0);
  vec3 face = kit(5.0, floor(hb * 3.99), l * 0.3 + 0.5 + h2(id, 73.0) * (SPANC - 1.6));
  face *= 0.84 + 0.3 * h1(id, 75.0);
  if (hb > 0.82) face *= vec3(0.86, 0.8, 0.8);
  face *= 0.9 + 0.1 * smoothstep(0.08, 0.3, e);
  return mix(face, vec3(0.33, 0.28, 0.25), 1.0 - smoothstep(0.05, 0.11, e));
}
// stones that read: a Worley field at about 7 per cell (3-4 cm stones at 0.5 m cells), each with its own tone, some
// brick red, lit from the upper left with a shadow on the lower right; cover = share of the ground they take
vec3 gravelStones(vec2 c, vec3 base, float cover) {
  vec2 q = c * 7.0, gq = floor(q); float d1 = 9.0; vec2 id = vec2(0.0), off = vec2(0.0);
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) { vec2 g = gq + vec2(float(i), float(j)); vec2 o = g + 0.15 + 0.7 * h2(g, 201.0) - q; float dd = length(o * vec2(1.0, 1.0 + 0.6 * h1(g, 203.0))); if (dd < d1) { d1 = dd; id = g; off = o; } }
  float r = 0.28 + 0.2 * h1(id, 205.0);
  if (h1(id, 207.0) > cover || d1 > r) return base * (1.0 - 0.18 * smoothstep(r + 0.12, r, d1) * step(h1(id, 207.0), cover));
  float t = h1(id, 209.0);
  vec3 sc = t < 0.12 ? vec3(0.56, 0.27, 0.2) : t < 0.2 ? vec3(0.42, 0.22, 0.17) : mix(vec3(0.32, 0.31, 0.3), vec3(0.66, 0.63, 0.58), h1(id, 211.0));
  float lit = clamp(0.5 + 1.6 * dot(-off / max(r, 1e-3), vec2(-0.6, -0.8)) * 0.5, 0.0, 1.0);
  return sc * (0.68 + 0.55 * lit) * (1.0 - 0.35 * smoothstep(r * 0.7, r, d1));
}
vec2 wuv(vec2 c) { return (c - uOrg.xy) / NW; }
float lvl(vec4 a, vec4 b, float L) { return L < 1.5 ? a.r : L < 2.5 ? a.g : L < 3.5 ? a.b : L < 4.5 ? a.a : L < 5.5 ? b.r : L < 6.5 ? b.g : b.b; }
// the raw class of the cell holding c (255 outside the window or not kit ground)
float cellCls(vec2 c) {
  vec2 ci = floor(c) - uOrg.xy;
  if (ci.x < 0.0 || ci.y < 0.0 || ci.x >= NW || ci.y >= NW) return 255.0;
  return floor(texture(uCell, (ci + 0.5) / NW).r * 255.0 + 0.5);
}
float kerbF(vec2 c) { return texture(uF1, wuv(c)).b; }
vec4 dmAt(float x, float row) { return texture(uDM, vec2((x + 0.5) / DMW, (row + 0.5) / 3.0)) * 255.0; }
void decal(inout vec3 col, vec2 c, vec2 ctr, float slot, float ang, mat2 frame, float useFrame, float hv) {
  vec4 m = dmAt(slot, 2.0);
  float size = m.r / 16.0;
  vec2 p = c - ctr;
  if (useFrame > 0.5) p = frame * p;
  else if (mod(m.g, 2.0) > 0.5) p = mat2(cos(ang), -sin(ang), sin(ang), cos(ang)) * p;
  vec2 uv = p / size + 0.5;
  if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) return;
  vec2 so = vec2(mod(slot, DCOLS), floor(slot / DCOLS)) * DSLOT;
  vec4 d = texture(uDecal, (so + 2.0 + uv * (DSLOT - 4.0)) / DEC);
  if (d.a > 0.0) d.rgb /= d.a;                       // (the page is premultiplied on upload)
  if (mod(m.g, 4.0) > 1.5) d.rgb = col * clamp(dot(d.rgb, vec3(0.299, 0.587, 0.114)) / max(m.a / 255.0, 0.05), 0.35, 1.5);
  if (m.g > 3.5) d.rgb *= vec3(0.78 + 0.34 * hv, 0.76 + 0.3 * hv, 0.74 + 0.26 * fract(hv * 7.3)) * (0.8 + 0.35 * fract(hv * 3.1));   // a manhole: its own rust and grime
  col = mix(col, d.rgb, d.a);
}
float pick(vec4 tab, float h) { return floor(dmAt(tab.r + tab.a * 256.0 + floor(h * tab.g), 1.0).r + 0.5); }   // (start: r + 256 a)
void decals(inout vec3 col, vec2 c, float cls, float stripe) {
  vec2 g0 = floor(c / 4.0);
  for (int dy = -1; dy <= 1; dy++) for (int dx = -1; dx <= 1; dx++) {
    vec2 g = g0 + vec2(float(dx), float(dy));
    // oil: a dark soft blot on road and lot
    if (cls < 1.5) {
      vec2 oc = (g + 0.1 + 0.8 * h2(g, 61.0)) * 4.0;
      if (h1(g, 63.0) < 0.3 && abs(cellCls(oc) - cls) < 0.5) {
        float a = h1(g, 65.0) * 3.14159, r0 = 0.6 + h1(g, 67.0) * 0.7;
        vec2 p = mat2(cos(a), -sin(a), sin(a), cos(a)) * (c - oc) / vec2(r0, r0 * 0.6);
        float k = (1.0 - smoothstep(0.35, 1.0, length(p))) * (0.45 + 0.55 * vn(c * 2.5, 69.0));
        col *= 1.0 - 0.3 * k;
      }
    }
    if (uOpt.y < 0.5) continue;
    // the class's own decals
    vec2 ctr = (g + 0.15 + 0.7 * h2(g, 31.0)) * 4.0;
    if (abs(cellCls(ctr) - cls) < 0.5) {
      vec4 tab = dmAt(cls, 0.0);
      if (tab.g > 0.5 && h1(g, 33.0) * 255.0 < tab.b) { float sl = pick(tab, h1(g, 35.0)); if (!(stripe > 0.0 && mod(dmAt(sl, 2.0).g, 4.0) > 1.5)) decal(col, c, ctr, sl, h1(g, 37.0) * 6.2831853, mat2(1.0), 0.0, h1(g, 39.0)); }   // (no asphalt patch or pothole printed on a crossing stripe)
    }
    // a drain in the gutter, its long side along the kerb, its edge on the kerb line
    if (cls < 0.5) {
      vec4 tab = dmAt(9.0, 0.0);
      vec2 gc = (g + 0.1 + 0.8 * h2(g, 41.0)) * 4.0;
      if (tab.g > 0.5 && h1(g, 43.0) * 255.0 < tab.b && cellCls(gc) < 0.5) {
        float f0 = kerbF(gc);
        if (f0 > 0.04 && f0 < 0.49) {
          vec2 gr = vec2(kerbF(gc + vec2(0.3, 0.0)) - kerbF(gc - vec2(0.3, 0.0)), kerbF(gc + vec2(0.0, 0.3)) - kerbF(gc - vec2(0.0, 0.3))) / 0.6;
          float gl = length(gr);
          if (gl > 0.05) {
            vec2 nd = gr / gl; float slot = pick(tab, h1(g, 45.0));
            vec4 m = dmAt(slot, 2.0); float hw = m.r / 16.0 * m.b / 128.0 * 0.5;
            vec2 at = gc + nd * ((0.5 - f0) / gl - hw - 0.04);
            decal(col, c, at, slot, 0.0, mat2(nd.x, -nd.y, nd.y, nd.x), 1.0, 0.5);   // x across the kerb, y along it
          }
        }
      }
    }
    // leaves, on whatever they fell on
    if (uOpt.x > 0.5) {
      vec2 lc = (g + 0.1 + 0.8 * h2(g, 51.0)) * 4.0;
      vec4 tab = dmAt(8.0, 0.0);
      if (tab.g > 0.5 && h1(g, 53.0) * 255.0 < tab.b && abs(cellCls(lc) - cls) < 0.5 && (cls > 0.5 || kerbF(lc) > 0.02)) decal(col, c, lc, pick(tab, h1(g, 55.0)), h1(g, 57.0) * 6.2831853, mat2(1.0), 0.0, 0.5);
    }
  }
}
void main(void) {
  vec2 c = vec2((vW.x / 8.0 + vW.y / 4.0) * 0.5, (vW.y / 4.0 - vW.x / 8.0) * 0.5);
  vec2 ci = floor(c) - uOrg.xy;
  if (ci.x < ${f(B - 1)} || ci.y < ${f(B - 1)} || ci.x > ${f(B + CHUNK)} || ci.y > ${f(B + CHUNK)}) { finalColor = vec4(0.0); return; }
  vec4 cell = texture(uCell, (ci + 0.5) / NW);
  if (cell.r > 0.99) { finalColor = vec4(0.0); return; }
  vec2 uv = wuv(c);
  vec4 F1 = texture(uF1, uv), F2 = texture(uF2, uv);
  // the class: the highest level whose blurred field passes (a ragged threshold for soft ground)
  float cls = 0.0;
  // soft ground (alley gravel, grass, dirt) has no kerb. Round 3 (blind A/B lane5_2 lost twice: first a pasted strip, then
  // a smeared blotch): the hard ground's edge is TORN - its threshold is moved by angular chunks (a rotated 0.45-cell grid,
  // one offset each) as well as ragged noise, and the switch is crisp; the asphalt gets a pale crumbled lip a few cm wide
  // and the dirt a dark shadow under it; the gravel is stones that read (gravelStones) on a dirt toned apart from asphalt
  vec2 cq = mat2(0.83, 0.55, -0.55, 0.83) * c / 0.45;
  float chunk = (h1(floor(cq), 9.0) - 0.5) * 0.34 + (h1(floor(cq * 2.3 + 0.5), 10.0) - 0.5) * 0.14;
  float rag = (vn(c * 0.8, 3.0) - 0.5) * 0.3 + (vn(c * 2.3, 4.0) - 0.5) * 0.16 + chunk;
  float edgeD = 9.0;   // how far (in field units) the pixel is from the nearest soft edge, signed: + inside the soft ground
  for (int i = 1; i <= 7; i++) { float L = float(i); bool soft = i == 2 || i == 6 || i == 7; float v = lvl(F1, F2, L); float t = soft ? 0.5 + rag : 0.5; if (v > t) cls = L; if (soft && abs(v - t) < abs(edgeD)) edgeD = v - t; }
  vec3 col = (abs(cls - 5.0) < 0.5 ? pavers(c) : hexKit(cls, c)) * gain(cls);
  if (abs(cls - 2.0) < 0.5 || abs(cls - 7.0) < 0.5) col = gravelStones(c, col * vec3(1.06, 0.93, 0.8) * 0.92, abs(cls - 2.0) < 0.5 ? 0.5 : 0.22);
  if (cls < 1.5 && edgeD < 0.0 && edgeD > -0.05) col = mix(col, vec3(0.6, 0.58, 0.55) * (0.8 + 0.4 * vn(c * 9.0, 7.0)), 0.75);   // the crumbled lip
  if (cls > 1.5 && edgeD > 0.0 && edgeD < 0.06) col *= 0.62 + 0.38 * edgeD / 0.06;                                            // its shadow on the dirt
  if (cls < 1.5 && edgeD < 0.0 && edgeD > -0.14) { float st = vn(mat2(0.8, 0.6, -0.6, 0.8) * c * 9.0, 8.0); if (st > 0.8) col = gravelStones(c, col, 0.9); }   // a few stones out on it
  if (abs(cls - 1.0) < 0.5) {                         // a lot: gravel and dirt worked into it in drifts
    // (kept light: at 0.85 the drifts read as camouflage across a lane, k8d lane1_0 / lane4_1 / lane5_2)
    col = mix(col, hexKit(2.0, c), smoothstep(0.66, 0.86, vn(c / 7.0, 21.0)) * 0.5);
    col = mix(col, hexKit(7.0, c), smoothstep(0.7, 0.9, vn(c / 9.0, 22.0)) * 0.3);
  }
  if (cls < 0.5) {
    // round 3 (blind A/B: the asphalt read 'flat and evenly grainy' beside the plates): tone drift at 9 m and 3 m, dark
    // stains, and the tyre tracks of each lane - darker, polished bands 0.6 m wide in the wheel paths, laid across the road
    // from its nearer edge (a 2.5 m parking strip, then 3.5 m lanes, wheels at 0.9 and 2.6 m), off at junctions
    col *= 0.86 + 0.24 * vn(mat2(0.8, 0.6, -0.6, 0.8) * c / 18.0, 121.0) + 0.1 * (vn(c / 6.0, 123.0) - 0.5);
    col *= 1.0 - 0.16 * smoothstep(0.7, 0.85, vn(mat2(0.28, -0.96, 0.96, 0.28) * c / 5.0, 125.0));
    float o = cell.a * 255.0;
    if (o > 64.0) {
      bool ew = o > 192.0;
      float a2 = cell.b * 255.0 + (ew ? fract(c.y) : fract(c.x)) - 0.5 - 5.0;
      float tm = mod(a2, 7.0), band = max(1.0 - smoothstep(0.35, 0.65, abs(tm - 1.8)), 1.0 - smoothstep(0.35, 0.65, abs(tm - 5.2)));
      float along = ew ? c.x : c.y;
      col *= 1.0 - 0.13 * band * step(0.0, a2) * (0.6 + 0.4 * vn(vec2(along / 6.0, a2), 127.0));
    }
  }
  if (abs(cls - 4.0) < 0.5) {                         // sidewalk: 1.5 m slabs, a tone each, dark joints and grime along them
    vec2 sj = fract(c / 3.0); vec2 jd = min(sj, 1.0 - sj) * 3.0; float jm = min(jd.x, jd.y);
    col *= 0.93 + 0.12 * h1(floor(c / 3.0), 81.0);
    col *= 1.0 - 0.12 * (1.0 - smoothstep(0.04, 0.25, jm));
    col = mix(col, col * 0.52, 1.0 - smoothstep(0.025, 0.05, jm));
  }
  // the kerb: a bright lip on its road edge, its face where the camera sees it, the gutter's shadow
  float k3 = F1.b;
  if (k3 > 0.02 && k3 < 0.98 && cls < 3.5) {
    vec2 gr = vec2(kerbF(c + vec2(0.25, 0.0)) - kerbF(c - vec2(0.25, 0.0)), kerbF(c + vec2(0.0, 0.25)) - kerbF(c - vec2(0.0, 0.25))) / 0.5;
    float gl = max(length(gr), 1e-3), d = (k3 - 0.5) / gl;   // cells from the kerb's road edge, + on the kerb
    vec2 nOut = -gr / gl; float facing = dot(nOut, vec2(0.70710678));
    if (abs(cls - 3.0) < 0.5 && d < 0.14) col *= 1.0 + 0.32 * (1.0 - max(d, 0.0) / 0.14);
    if (cls < 2.5) {
      // the face: 15 cm of kerb stands Z_PX 26 x 0.15 = 3.9 px over the road on screen, and a cell toward the camera along
      // nOut is 4 (nOut.x + nOut.y) px down: that many cells of road are covered by the face where the camera sees it
      float sdn = 4.0 * (nOut.x + nOut.y), fw = sdn > 0.5 ? min(1.0, 3.9 / sdn) : 0.0;
      // (round 3: 'a flat light strip with no depth' at phone size: a darker face with a lit top edge and a dark foot, and a
      // longer, darker shadow on the road)
      if (fw > 0.0 && -d < fw) { float u = -d / fw; col = hexKit(3.0, c) * mix(0.46, 0.62, u) * (u < 0.15 ? 1.35 : 1.0) * (u > 0.85 ? 0.6 : 1.0); }
      else col *= mix(facing > 0.0 ? 0.45 : 0.7, 1.0, smoothstep(fw, fw + 1.3, -d));
    }
  }
  if (cls < 0.5) {
    // crossings: bars along the crossing's long axis, inset from its edge, worn
    // (the world's crossing cells ARE the bars, 2 cells of paint and 2 of road, on the cell lines: drawn as they are)
    // One continuous band of paint (blind A/B, n1800_0: the old wear noise had a ~1-cell lattice and let the asphalt show
    // through by up to 28%, so each stripe read as a checker of cells): one tone across the stripe, its grain from the
    // kerb concrete swatch (continuous, no lattice), worn through only in a few long scuffs (2.6-cell noise, stretched
    // along the traffic) where the asphalt shows; the crack decals run over it as they do over the road.
    // round 3 (blind A/B n1800_0: rounded ends and grey smudges 'sit on the road like stickers'): the stripe is the world's
    // crossing cells with SQUARE ends (read per cell, edged where the neighbour cell is not a crossing), and its wear is
    // paint gone, not grey on top: streaks along the traffic (across the stripe), where the asphalt and its cracks show
    vec2 cc = floor(c) - uOrg.xy, fq = fract(c);
    float isX = texture(uF2, (cc + 0.5) / NW).a;
    if (isX > 0.5) {
      float nl = texture(uF2, (cc + vec2(-0.5, 0.5)) / NW).a, nr = texture(uF2, (cc + vec2(1.5, 0.5)) / NW).a;
      float nu = texture(uF2, (cc + vec2(0.5, -0.5)) / NW).a, nd2 = texture(uF2, (cc + vec2(0.5, 1.5)) / NW).a;
      float e = 1.0;
      if (nl < 0.5) e *= smoothstep(0.0, 0.05, fq.x); if (nr < 0.5) e *= smoothstep(0.0, 0.05, 1.0 - fq.x);
      if (nu < 0.5) e *= smoothstep(0.0, 0.05, fq.y); if (nd2 < 0.5) e *= smoothstep(0.0, 0.05, 1.0 - fq.y);
      bool alongY = nu > 0.5 || nd2 > 0.5;           // the stripe runs along y, so traffic runs along x
      float tr = alongY ? c.x : c.y, ac = alongY ? c.y : c.x;
      float grain = dot(hexKit(3.0, c * 1.7), vec3(0.333)) / 0.72;
      vec3 paint = vec3(0.86, 0.86, 0.82) * clamp(0.9 + 0.36 * (grain - 1.0), 0.82, 1.04);
      // (r3 first try: streaks 0.07-0.17 cells across read as a barcode; now 0.4-cell streaks, 1-3 m long, ~20% gone,
      // mostly where the wheels run)
      float streak = vn(vec2(tr * 0.35, ac * 2.4), 91.0) * 0.7 + vn(vec2(tr * 1.1, ac * 5.0), 92.0) * 0.3;
      float wheel = smoothstep(0.35, 0.85, vn(vec2(ac / 1.8, 0.5), 93.0));        // the wheel paths
      float gone = smoothstep(0.66, 0.7, streak * (0.6 + 0.5 * wheel));
      col = mix(col, paint, e * (1.0 - gone));
    }
    // lines: signed distance to the fitted polylines (yellow: a double line; white: a single one)
    vec4 Ln = texture(uLine, uv);
    float dyl = Ln.g > 0.99 ? abs(Ln.r - 0.5) * 2.0 : 9.0, dwl = Ln.a > 0.99 ? abs(Ln.b - 0.5) * 2.0 : 9.0;
    float wr = (0.7 + 0.3 * smoothstep(0.25, 0.6, vn(c * 0.4, 93.0))) * (0.86 + 0.14 * vn(c * 3.3, 94.0));   // (no ~1-cell lattice: it read as tiles)
    col = mix(col, vec3(0.86, 0.7, 0.22), (1.0 - smoothstep(0.085, 0.115, abs(dyl - 0.2))) * wr);   // (10 cm lines, 10 cm apart)
    col = mix(col, vec3(0.82, 0.82, 0.78), (1.0 - smoothstep(0.08, 0.11, dwl)) * wr);
  }
  decals(col, c, cls, cls < 0.5 ? step(0.55, F2.a) : 0.0);
  // the 6 m light-and-colour drift (as the engine ground had: research 2026-10-31 R8)
  float mk = 0.88 + vn(c / 12.0, 141.0) * 0.14 + vn(c / 3.0, 143.0) * 0.06, mt = (vn(c / 20.0, 147.0) - 0.5) * 0.08;
  col *= vec3(mk * (1.0 + mt), mk, mk * (1.0 - mt));
  // contact shadow under a wall to the north or west (the light comes from the north-east), as before
  vec2 fr = fract(c);
  float wf = floor(cell.g * 255.0 + 0.5);
  if (wf >= 127.5 && fr.y < 0.6) col *= 0.78 + fr.y * 0.3;
  if (mod(wf, 128.0) >= 63.5 && fr.x < 0.4) col *= 0.85 + fr.x * 0.35;
  // the world's edge: the skirt fades into the night
  float fd = texture(uF3, uv).r;
  if (fd < 0.999) { float fz = clamp(fd + (vn(c / 4.0, 161.0) - 0.5) * 0.25, 0.0, 1.0); fz = fz * fz * (3.0 - 2.0 * fz) * 0.85; col = NIGHT + (col - NIGHT) * fz; }
  finalColor = vec4(col, 1.0);
}`;
}

export class GroundKit {
  ok = false;
  private kitTex: Texture | null = null; private decTex: Texture | null = null; private dm: Texture | null = null;
  private shader: Shader | null = null; private geo: Geometry | null = null; private mesh: Mesh<Geometry, Shader> | null = null;
  private prep: Prep | null = null;
  private src: Record<'cell' | 'f1' | 'f2' | 'f3' | 'line', { u8: Uint8Array; s: BufferImageSource }> | null = null;
  private uni = new UniformGroup({ uOrg: { value: new Float32Array(4), type: 'vec4<f32>' }, uOpt: { value: new Float32Array(4), type: 'vec4<f32>' } });
  /** fall leaves (the world's season) */
  leaves = true;
  private kern = kernel();

  async load(base = 'art/iso/') {
    try {
      const [kj, dj] = await Promise.all([fetch(base + 'ground_kit.json').then(r => r.json()), fetch(base + 'ground_decals.json').then(r => r.json())]);
      // NOT mipmapped: across a hex edge a pixel quad's texture coordinates jump between patches, the implicit mip level
      // jumps with them, and every triangle edge drew a line (k3d, 2026-11-01). The kit is supersampled instead (kitAA)
      const [kt, dt] = await Promise.all([base + 'ground_kit.jpg', base + 'ground_decals.png'].map(src =>
        Assets.load<Texture>({ src, data: { autoGenerateMipmaps: false, scaleMode: 'linear' } })));
      if (kj.gut !== 10 || kj.slot !== 320) throw new Error('ground kit layout changed');
      this.kitTex = kt; this.decTex = dt;
      const atl = CLASSES.map(c => (kj.classes as string[]).indexOf(c));
      if (atl.some(i => i < 0)) throw new Error('ground kit is missing a class');
      const { d, W } = decalTable(dj.decals, DENS);
      this.dm = new Texture({ source: new BufferImageSource({ resource: d, width: W, height: 3, format: 'rgba8unorm', scaleMode: 'nearest', alphaMode: 'no-premultiply-alpha' }) });
      const mk = (w: number, lin: boolean) => { const u8 = new Uint8Array(w * w * 4); return { u8, s: new BufferImageSource({ resource: u8, width: w, height: w, format: 'rgba8unorm', scaleMode: lin ? 'linear' : 'nearest', addressMode: 'clamp-to-edge', alphaMode: 'no-premultiply-alpha' }) }; };
      this.src = { cell: mk(N, false), f1: mk(N, true), f2: mk(N, true), f3: mk(N, true), line: mk(LN, true) };
      this.shader = new Shader({
        glProgram: new GlProgram({ vertex: VERT, fragment: fragSrc(atl, kj.size[0], kj.size[1], dj.size[0], dj.size[1], dj.cols, dj.slot, W), name: 'dm-groundkit', preferredFragmentPrecision: 'highp' }),
        resources: { gk: this.uni, uCell: this.src.cell.s, uF1: this.src.f1.s, uF2: this.src.f2.s, uF3: this.src.f3.s, uLine: this.src.line.s, uKit: kt.source, uDecal: dt.source, uDM: this.dm.source },
      });
      this.geo = new Geometry({ attributes: { aPosition: new Float32Array(8), aW: new Float32Array(8) }, indexBuffer: [0, 1, 2, 0, 2, 3] });
      this.mesh = new Mesh({ geometry: this.geo, shader: this.shader });
      this.ok = true;
    } catch (e) { console.warn('ground kit: not loaded, the engine painter draws the street', e); this.ok = false; }
  }

  private fill(map: IsoMap, kx: number, ky: number) {
    if (!this.prep || this.prep.map !== map) this.prep = new Prep(map);
    const S = this.src!;
    if (!chunkData(this.prep, this.kern, kx, ky, { cell: S.cell.u8, f1: S.f1.u8, f2: S.f2.u8, f3: S.f3.u8, line: S.line.u8 })) return false;
    for (const k of ['cell', 'f1', 'f2', 'f3', 'line'] as const) S[k].s.update();
    const u = this.uni.uniforms.uOrg as Float32Array; u[0] = kx * CHUNK - B; u[1] = ky * CHUNK - B; u[2] = N; u[3] = 0;
    const o = this.uni.uniforms.uOpt as Float32Array; o[0] = this.leaves ? 1 : 0; o[1] = 1;
    this.uni.update();
    return true;
  }

  /** chunk (kx, ky)'s ground as a texture 512 x 256 logical px: the kit over the indoor floors the CPU painted (indoor, or
   *  null when the chunk has none). null when the chunk has no kit ground at all (the caller keeps its CPU canvas) */
  bake(renderer: Renderer, map: IsoMap, kx: number, ky: number, indoor: HTMLCanvasElement | null): RenderTexture | null {
    if (!this.ok || !this.fill(map, kx, ky)) return null;
    const Wl = CHUNK * 16, Hl = CHUNK * 8;
    const rt = RenderTexture.create({ width: Wl, height: Hl, resolution: GRES, scaleMode: 'linear' });
    if (indoor) { const s = new Sprite(Texture.from(indoor)); renderer.render({ container: s, target: rt, clear: true }); s.destroy({ texture: true, textureSource: true }); }
    const sx0 = (kx * CHUNK - ky * CHUNK - CHUNK) * 8, sy0 = (kx * CHUNK + ky * CHUNK) * 4;
    const pos = this.geo!.getAttribute('aPosition').buffer, wb = this.geo!.getAttribute('aW').buffer;
    pos.data = new Float32Array([0, 0, Wl, 0, Wl, Hl, 0, Hl]);
    wb.data = new Float32Array([sx0, sy0, sx0 + Wl, sy0, sx0 + Wl, sy0 + Hl, sx0, sy0 + Hl]);
    renderer.render({ container: this.mesh!, target: rt, clear: !indoor });
    return rt;
  }
}
